import { Stack } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router';

import { PipelineListItemNameContainer } from '@/console/features/card-layout/components/recent-pipelines-card/components/pipeline-card-row/pipeline-card-row.styled';
import { TPipelineCardRowProps } from '@/console/features/card-layout/components/recent-pipelines-card/components/pipeline-card-row/pipeline-card-row.types';
import { PipelineExecutionStatus } from '@/console/features/pipelines/components/pipeline-execution-status/pipeline-execution-status';
import { TPipelineStepOverview } from '@/console/features/pipelines/components/pipeline-step-overview/pipeline-step-overview.types';
import { PipelineStepsOverview } from '@/console/features/pipelines/components/pipeline-steps-overview/pipeline-steps-overview';
import { PipelineTriggerType } from '@/console/features/pipelines/components/pipeline-trigger-type/pipeline-trigger-type';
import { useLatestPipelineExecutionsContext } from '@/console/features/pipelines/hooks/use-latest-pipeline-executions/latest-pipeline-executions.context';
import { DotIcon } from '@/shared/assets/icons/dot-icon';
import { OpenIcon } from '@/shared/assets/icons/open-icon';
import { SearchableResourceField } from '@/shared/components/ui/searchable-resource-field/searchable-resource-field';
import { TruncatedTypography } from '@/shared/components/ui/truncated-text/truncated-text.styled';
import { useJobStatus } from '@/shared/features/jobs/hooks/use-job-status/use-job-status';
import {
  jobsApi,
  useReadPipelineExecutionsForPipelineQuery,
} from '@/shared/redux/api/jobs/jobs-api';

export const PipelineCardRow: React.FC<TPipelineCardRowProps> = ({
  pipeline,
}) => {
  const { getStatusColor } = useJobStatus();
  const batchedExecutions = useLatestPipelineExecutionsContext();
  const isBatched = batchedExecutions.isEnabled;

  const { data } = useReadPipelineExecutionsForPipelineQuery(
    {
      pipelineConfigurationId: pipeline.pipelineConfigurationId,
      pagination: { pageSize: 1, page: 1 },
    },
    { skip: isBatched },
  );
  const prefetchPipeline = jobsApi.usePrefetch('ReadPipeline');

  const navigate = useNavigate();
  const pipelineExecution = isBatched
    ? batchedExecutions.getExecution(pipeline.pipelineConfigurationId)
    : data?.readAllPipelineExecutionsForConfiguration.items[0];

  const navigateToPipeline = () =>
    navigate(`/pipelines/${pipeline.pipelineConfigurationId}`);

  const getPipelineSteps = (): TPipelineStepOverview[] => {
    return pipelineExecution?.steps ?? pipeline.steps;
  };

  return (
    <Stack
      onClick={navigateToPipeline}
      onMouseEnter={() =>
        prefetchPipeline({
          pipelineConfigurationId: pipeline.pipelineConfigurationId,
        })
      }
      direction="row"
      justifyContent="space-between"
    >
      <Stack
        height="60px"
        direction="row"
        width="100%"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack gap="0.5rem" width="300px">
          <PipelineListItemNameContainer
            direction="row"
            gap="0.5rem"
            alignItems="center"
            $status={getStatusColor(pipelineExecution?.status)}
          >
            <DotIcon />
            <TruncatedTypography variant="p2">
              <SearchableResourceField
                maxLength={16}
                value={pipeline.name}
                queryParam="name"
              />
            </TruncatedTypography>
          </PipelineListItemNameContainer>
          <PipelineTriggerType
            trigger={pipeline.trigger}
            variant="p10"
            color="textSecondary"
          />
        </Stack>
        <PipelineExecutionStatus
          pipelineConfigurationId={
            isBatched ? undefined : pipeline.pipelineConfigurationId
          }
          execution={isBatched ? pipelineExecution : undefined}
          isLoading={isBatched && batchedExecutions.isLoading}
        />
        <PipelineStepsOverview steps={getPipelineSteps()} />
      </Stack>
      <OpenIcon variant="light" />
    </Stack>
  );
};
